import { Injectable } from '@angular/core';
import { RagDocument } from '../models/rag.models';

/**
 * IndexedDB-backed persistence for RAG documents.
 *
 * Documents (including their chunks and embeddings) survive page refreshes,
 * so the RAG index does not have to be rebuilt from scratch every load.
 *
 * Database layout:
 *   name:     'ragStore'
 *   version:  1
 *   store:    'documents'  (keyPath: 'id')
 */
const DB_NAME = 'ragStore';
const DB_VERSION = 1;
const STORE_NAME = 'documents';

@Injectable({ providedIn: 'root' })
export class RagPersistenceService {
  private db: IDBDatabase | null = null;

  /**
   * Lazily opens the database, creating the object store on first upgrade.
   * Caches the connection so subsequent operations reuse it.
   */
  private openDb(): Promise<IDBDatabase> {
    if (this.db) {
      return Promise.resolve(this.db);
    }

    return new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available in this environment'));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        // If a newer version of the app opens the DB elsewhere, drop this
        // stale connection so we can re-open with the new schema.
        this.db.onversionchange = () => {
          this.db?.close();
          this.db = null;
        };
        resolve(this.db);
      };

      request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
      request.onblocked = () => reject(new Error('IndexedDB open was blocked'));
    });
  }

  /**
   * Runs `fn` inside a transaction against the documents store and resolves
   * with the request result (or rejects on error).
   */
  private tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> {
    return this.openDb().then(
      (db) =>
        new Promise<T>((resolve, reject) => {
          const store = db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
          const request = fn(store);
          request.onsuccess = () => resolve(request.result as T);
          request.onerror = () => reject(request.error ?? new Error('IndexedDB operation failed'));
        }),
    );
  }

  /** Load all persisted documents (empty array on any failure). */
  async loadAll(): Promise<RagDocument[]> {
    try {
      return await this.tx<RagDocument[]>('readonly', (store) => store.getAll());
    } catch (error) {
      console.error('rag-persistence: failed to load documents', error);
      return [];
    }
  }

  /** Create or overwrite a single document. */
  async saveDocument(doc: RagDocument): Promise<void> {
    try {
      await this.tx<unknown>('readwrite', (store) => store.put(doc));
    } catch (error) {
      console.error('rag-persistence: failed to save document', doc?.id, error);
    }
  }

  /** Delete a single document by id. */
  async deleteDocument(id: string): Promise<void> {
    try {
      await this.tx<unknown>('readwrite', (store) => store.delete(id));
    } catch (error) {
      console.error('rag-persistence: failed to delete document', id, error);
    }
  }

  /** Remove all documents. */
  async clearAll(): Promise<void> {
    try {
      await this.tx<unknown>('readwrite', (store) => store.clear());
    } catch (error) {
      console.error('rag-persistence: failed to clear documents', error);
    }
  }
}
